//#region imports
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  getAllTitles,
  getTitleByAgencyId,
  updateTitle,
} from '../../../features/state/StateTitle-slice';
// // eslint-disable-next-line @typescript-eslint/no-unused-vars
import { getChaptersByTitleId, updateChapter } from '../../../features/state/StateChapter-slice';
import { getRecordStatuses } from '../../../features/state/StateRecordStatus-slice';
import { getAllAgencies } from '../../../features/state/StateAgency-slice';
import { Title } from '../../../utilities/interfaces/Title';
import { Chapter } from '../../../utilities/interfaces/Chapter';
import { Agency } from '../../../utilities/interfaces/Agency';
import { chapterFileAPI } from '../../../utilities/api/chapterFileApi';
import { useNotificationHelper } from '../../../utilities/helpers/NotificationHelper';
import { BlobContainer } from '../../../utilities/enums/BlobContainer';
import { ChapterFile } from '../../../utilities/interfaces/ChapterFile';
import { RecordStatus } from '../../../utilities/interfaces/RecordStatus';
import { emergencyRegulationApi } from '../../../utilities/api/emergencyRegulationApi';
import { Roles } from '../../../utilities/enums/Roles';

//#endregion

export const useCodeManagementControls = () => {
  //#region redux state
  const titleValues: Title[] = useAppSelector((state) => state.titles.value);
  const titleList: Title[] = useAppSelector((state) => state.titles.titleList);
  const chapterValues: Chapter[] = useAppSelector((state) => state.chapters.chapters);
  const agencyValues: Agency[] = useAppSelector((state) => state.agency.agencies);
  const recordStatuses: RecordStatus[] = useAppSelector(
    (state) => state.recordStatuses.recordStatuses
  );
  const dispatch = useAppDispatch();
  //#endregion

  //#region notification helper
  const { addNotification } = useNotificationHelper();
  //#endregion

  //#region runtime temporary stateful items
  const [selectedAgency, setSelectedAgency] = useState<any>();
  const [selectedTitle, setSelectedTitle] = useState<any>();
  const [selectedChapter, setSelectedChapter] = useState<any>();
  const [selectedRecordStatus, setSelectedRecordStatus] = useState<any>();
  const [selectedDateFiled, setSelectedDateFiled] = useState<any>();
  const [selectedEffectiveDate, setSelectedEffectiveDate] = useState<any>();
  // const [selectedRecordStatus, setSelectedRecordStatus] = useState<any>();
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [chapterFiles, setChapterFiles] = useState<any>();
  const [chaptersLoading, setChaptersLoading] = useState<boolean>(false);
  const [editTitleOpen, setEditTitleOpen] = useState<boolean>(false);
  const [addAgencyOpen, setAddAgencyOpen] = useState<boolean>(false);
  const [addTitleOpen, setAddTitleOpen] = useState<boolean>(false);
  const [editAgencyOpen, setEditAgencyOpen] = useState<boolean>(false);
  //#endregion

  // #region dropdown functions
  const handleTitleSelectChange = async (event: any) => {
    setSelectedTitle(event.value);
    setSelectedChapter(null);
    setSelectedRecordStatus(null);
    setChapterFiles(null);
    setSelectedDateFiled(undefined);
    setSelectedEffectiveDate(undefined);
    setChaptersLoading(true);
    await dispatch(getChaptersByTitleId(event.value.id));
    setChaptersLoading(false);
  };

  const handleAgencySelectChange = (event: any) => {
    setSelectedAgency(event.value);
    setSelectedTitle(null);
    setSelectedChapter(null);
    setSelectedRecordStatus(null);
    setSelectedDateFiled(undefined);
    setSelectedEffectiveDate(undefined);
    setChapterFiles(null);
    dispatch(getTitleByAgencyId(event.value.id));
  };
  //#endregion

  //#region chapter functions
  // gets set on a chapter being selected to edit
  const handleChapterSelectChange = async (chapter: any) => {
    setSelectedChapter(chapter);
    setSelectedRecordStatus(recordStatuses.find((x) => x.id === chapter.recordStatusId));

    if (chapter.dateFiled === null) setSelectedDateFiled(undefined);
    else setSelectedDateFiled(new Date(chapter.dateFiled));

    if (chapter.effectiveDate === null) setSelectedEffectiveDate(undefined);
    else setSelectedEffectiveDate(new Date(chapter.effectiveDate));

    setChapterFiles(null);
    let files = await chapterFileAPI.getChapterFilesByChapterId(chapter.id);
    setChapterFiles(files);
  };

  // updates on chapter text field being updated
  const handleChapterNameChange = (name: string) => {
    let chapter = { ...selectedChapter, chapterName: name };
    setSelectedChapter(chapter);
  };

  // updates chapter number
  const handleChapterNumberChange = (chapterNumber: string) => {
    let chapter = { ...selectedChapter, chapterNumber: chapterNumber };
    setSelectedChapter(chapter);
  };

  // updates the record status
  const handleRecordStatusChange = (event: any) => {
    setSelectedRecordStatus(event.value);
    let chapter = { ...selectedChapter, recordStatusId: event.value.id };
    setSelectedChapter(chapter);
  };

  // updates the date filed
  const handleDateFiledChange = (event: any) => {
    setSelectedDateFiled(event.value);
    let chapter = { ...selectedChapter, dateFiled: event.value.toISOString() };
    setSelectedChapter(chapter);
  };

  // updates the effective date
  const handleEffectiveDateChange = (event: any) => {
    setSelectedEffectiveDate(event.value);
    let chapter = { ...selectedChapter, effectiveDate: event.value.toISOString() };
    setSelectedChapter(chapter);
  };

  // save function in the edit chapter view
  const onSave = async (text: string) => {
    // this btoa error can be safely ignored
    let chapter = { ...selectedChapter, chapterHtml: text };
    dispatch(updateChapter(chapter))
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          message: `${chapter.chapterName} was saved successfully`,
        });
        toggleEdit();
      })
      .catch((error: any) => {
        addNotification({
          type: 'error',
          message: `${chapter.chapterName} was unable to save. ${error.message}`,
        });
      });
  };
  // #endregion

  //#region fileUpload
  const handleFileUploadResponseMessage = (message: string, type: 'error' | 'success') => {
    addNotification({
      type: type,
      message: message,
    });
  };

  // File Upload and Delete functions
  const saveFile = async (file: any) => {
    let ext = file.blobName.split('.').pop();
    let chapterImage: ChapterFile = {
      chapterId: selectedChapter.id,
      originalName: file.fileName,
      blobName: file.blobName,
      container: BlobContainer.chapterImageContainer,
      fileExtension: ext,
    };

    try {
      await chapterFileAPI.createChapterFile(chapterImage);
      addNotification({ type: 'success', message: 'File successfully added to chapter' });
    } catch (error: any) {
      addNotification({
        type: 'error',
        message: `File failed to save on chapter: ${error.message}`,
      });
    }
  };

  const deleteFile = async (file: any) => {
    if (file[0].id) {
      chapterFileAPI.deleteById(file[0].id);
    } else {
      chapterFileAPI.deleteByBlobName(file[0].blobName);
    }
  };
  //#endregion

  //#region edit view
  // switches to the edit view
  const toggleEdit = () => {
    editOpen ? setEditOpen(false) : setEditOpen(true);
  };

  const toggleEditTitleDialog = () => {
    editTitleOpen ? setEditTitleOpen(false) : setEditTitleOpen(true);
  };

  //#endregion

  //#region title update
  // TODO: this needs to call and update title endpoint and save the modified title data endpoint currently doesn't exist
  const updateAgencyTitle = async () => {
    let response = await dispatch(updateTitle(selectedTitle));
    if (response.type === 'stateTitle/updateTitle/fulfilled') {
      addNotification({ type: 'success', message: 'Title saved successfully' });
    } else {
      addNotification({ type: 'success', message: 'Title saved successfully' });
    }
    toggleEditTitleDialog();
  };

  const handleTitleNameChange = (titleName: string) => {
    let tempTitle = { ...selectedTitle };
    if (tempTitle) {
      tempTitle.titleName = titleName;
      setSelectedTitle(tempTitle);
    }
  };

  const handleTitleNumberChange = (titleNum: number) => {
    let tempTitle = { ...selectedTitle };
    if (tempTitle) {
      tempTitle.titleNumber = titleNum;
      setSelectedTitle(tempTitle);
    }
  };
  //#endregion

  //#region emergency regulation view

  const [emergencyRegulationOpen, setEmergencyRegulationOpen] = useState<boolean>(false);

  const toggleEmergencyRegulation = (emergencyRegulationId?: number) => {
    !emergencyRegulationOpen &&
      emergencyRegulationId &&
      getEmergencyRegulation(emergencyRegulationId);
    emergencyRegulationOpen ? setEmergencyRegulationOpen(false) : setEmergencyRegulationOpen(true);
    !emergencyRegulationOpen && setRenew(false);
  };

  const [emergencyRegulation, setEmergencyRegulation] = useState<any>();

  const getEmergencyRegulation = async (emergencyRegulationId: number) => {
    setEmergencyRegulation(await emergencyRegulationApi.getById(emergencyRegulationId));
  };

  const [renew, setRenew] = useState<boolean>(false);

  const toggleRenew = () => {
    setRenew(!renew);
  };

  const [renewDate, setRenewDate] = useState<Date | null>();

  const handleRenewDateChange = (date: Date | null) => {
    setRenewDate(date);
  };

  const [renewed, setRenewed] = useState<boolean>(false);

  const confirmRenewDate = () => {
    emergencyRegulationApi
      .renewEmergencyRegulation({
        emergencyRegulationId: emergencyRegulation.id,
        renewalDate: renewDate,
      })
      .then(() => {
        setRenewed(true);
      });
  };

  //#endregion

  //#region create title or agency
  // const [addTitleOpen, setAddTitleOpen] = useState<boolean>(false);
  const toggleAgencyAddDialog = () => {
    addAgencyOpen ? setAddAgencyOpen(false) : setAddAgencyOpen(true);
  };
  const toggleTitleAddDialog = () => {
    addTitleOpen ? setAddTitleOpen(false) : setAddTitleOpen(true);
  };
  const toggleAgencyEditDialog = () => {
    editAgencyOpen ? setEditAgencyOpen(false) : setEditAgencyOpen(true);
  };
  //#endregion

  //#region check is sos user

  const [isSosUser, setIsSosUser] = useState<boolean>(false);

  useEffect(() => {
    let userRoles = sessionStorage.getItem('roles');
    if (!!userRoles) {
      let parsedUserRoles: any[] = JSON.parse(userRoles);
      if (parsedUserRoles.includes(Roles.SosOffice)) {
        setIsSosUser(true);
      }
    }
  }, []);

  //#endregion

  return {
    // functions
    saveFile,
    deleteFile,
    handleTitleSelectChange,
    onSave,
    handleAgencySelectChange,
    handleChapterSelectChange,
    handleChapterNameChange,
    handleChapterNumberChange,
    toggleEdit,
    toggleEmergencyRegulation,
    toggleRenew,
    toggleAgencyAddDialog,
    toggleAgencyEditDialog,
    toggleTitleAddDialog,
    handleFileUploadResponseMessage,
    handleTitleNameChange,
    handleTitleNumberChange,
    handleRecordStatusChange,
    handleDateFiledChange,
    handleEffectiveDateChange,
    handleRenewDateChange,
    confirmRenewDate,
    //redux
    getAllAgencies,
    getAllTitles,
    getRecordStatuses,
    // state
    titleValues,
    titleList,
    recordStatuses,
    chapterValues,
    agencyValues,
    dispatch,
    selectedAgency,
    selectedTitle,
    selectedChapter,
    selectedRecordStatus,
    selectedDateFiled,
    selectedEffectiveDate,
    editOpen,
    chapterFiles,
    chaptersLoading,
    editTitleOpen,
    editAgencyOpen,
    emergencyRegulationOpen,
    emergencyRegulation,
    renew,
    renewDate,
    renewed,
    toggleEditTitleDialog,
    updateAgencyTitle,
    addAgencyOpen,
    addTitleOpen,
    isSosUser,
  };
};
